// Memos view — template gallery on the left, rendered memo on the right.
//
// Click a template → render markdown via lib/memos. Click "Open editable" →
// fork the markdown into a textarea so the user can edit before copying or
// downloading. "Copy" puts the current text on the clipboard. "Download"
// drops a .md file.

import {
  generateMemo,
  MEMO_TEMPLATES,
  type MemoContext,
  type MemoKind,
  type MemoTemplate,
} from "../lib/memos.ts";
import { renderMarkdown } from "../lib/markdown.ts";
import { composeTopic, loadGraph, loadMovements, type PeriodScope } from "../lib/queries.ts";
import type { TopicWithContext } from "../lib/types.ts";

interface State {
  graph: Awaited<ReturnType<typeof loadGraph>> | null;
  ctxs: TopicWithContext[];
  movements: import("../lib/types.ts").ScoreMovement[];
  active: MemoTemplate;
  topic: TopicWithContext | null;
  /** Current rendered markdown — either generated or user-edited. */
  markdown: string;
  /** True after the user clicked "Open editable" — toggles to <textarea>. */
  editing: boolean;
}

export function renderMemos(
  host: HTMLElement,
  period: PeriodScope,
  pinTopicHook?: (id: number) => void,
): () => void {
  void pinTopicHook;
  const state: State = {
    graph: null,
    ctxs: [],
    movements: [],
    active: MEMO_TEMPLATES[0],
    topic: null,
    markdown: "Loading…",
    editing: false,
  };

  let cancelled = false;
  host.innerHTML = `<div class="memos-loading muted">Loading memo data…</div>`;

  const buildContext = (): MemoContext | null => {
    if (!state.graph) return null;
    return {
      ctxs: state.ctxs,
      movements: state.movements,
      generatedAt: new Date().toISOString(),
      period,
      topic: state.topic,
      businessLines: state.graph.businessLines,
      categories: state.graph.categories,
    } as MemoContext;
  };

  const regenerate = () => {
    const ctx = buildContext();
    if (!ctx) return;
    state.markdown = generateMemo(state.active.kind, ctx);
    state.editing = false;
    paint();
  };

  const paint = () => {
    if (!state.graph) return;
    const tplList = MEMO_TEMPLATES.map((t) => `
      <button class="memo-template ${state.active.kind === t.kind ? "active" : ""}" data-kind="${t.kind}">
        <div class="memo-template-head">
          <span class="memo-template-cadence">${t.cadence}</span>
          <span class="memo-template-title">${t.title}</span>
        </div>
        <div class="memo-template-desc">${t.description}</div>
      </button>
    `).join("");

    const topicPicker = state.active.topicScoped
      ? `
        <div class="memo-topic-picker">
          <label class="memo-topic-label">Topic</label>
          <select id="memoTopicSelect">
            <option value="">— select a topic —</option>
            ${state.ctxs
              .slice()
              .sort((a, b) => a.topic.name.localeCompare(b.topic.name))
              .map((c) => `<option value="${c.topic.id}" ${state.topic?.topic.id === c.topic.id ? "selected" : ""}>${escapeHtml(c.topic.name)}</option>`)
              .join("")}
          </select>
        </div>
      `
      : "";

    const body = state.editing
      ? `<textarea class="memo-editor" id="memoEditor" spellcheck="true">${escapeHtml(state.markdown)}</textarea>`
      : `<article class="memo-rendered md-body">${renderMarkdown(state.markdown)}</article>`;

    const editToggle = state.editing
      ? `<button class="btn" id="memoPreview">Preview</button>`
      : `<button class="btn" id="memoEdit">Open editable</button>`;

    host.innerHTML = `
      <div class="memos">
        <aside class="memos-rail">
          <header class="memos-rail-head">
            <h3>Templates</h3>
            <span class="muted">click to render</span>
          </header>
          <div class="memo-template-list">${tplList}</div>
          ${topicPicker}
        </aside>
        <main class="memos-main">
          <header class="memos-main-head">
            <div>
              <h2 class="memos-title">${escapeHtml(state.active.title)}</h2>
              <span class="memos-meta">${escapeHtml(state.active.cadence)} · ${escapeHtml(state.active.description)}</span>
            </div>
            <div class="memos-actions">
              ${editToggle}
              <button class="btn" id="memoCopy">Copy</button>
              <button class="btn btn-primary" id="memoDownload">Download .md</button>
            </div>
          </header>
          <div class="memos-canvas">${body}</div>
        </main>
      </div>
    `;

    // Wire handlers.
    host.querySelectorAll<HTMLButtonElement>(".memo-template").forEach((btn) => {
      btn.addEventListener("click", () => {
        const kind = btn.dataset.kind as MemoKind;
        const tpl = MEMO_TEMPLATES.find((t) => t.kind === kind);
        if (!tpl) return;
        state.active = tpl;
        regenerate();
      });
    });

    host.querySelector<HTMLSelectElement>("#memoTopicSelect")?.addEventListener("change", (e) => {
      const v = (e.target as HTMLSelectElement).value;
      const id = v ? Number(v) : null;
      state.topic = id ? state.ctxs.find((c) => c.topic.id === id) ?? null : null;
      regenerate();
    });

    host.querySelector<HTMLButtonElement>("#memoEdit")?.addEventListener("click", () => {
      state.editing = true;
      paint();
    });
    host.querySelector<HTMLButtonElement>("#memoPreview")?.addEventListener("click", () => {
      const ta = host.querySelector<HTMLTextAreaElement>("#memoEditor");
      if (ta) state.markdown = ta.value;
      state.editing = false;
      paint();
    });

    host.querySelector<HTMLButtonElement>("#memoCopy")?.addEventListener("click", async () => {
      const ta = host.querySelector<HTMLTextAreaElement>("#memoEditor");
      const text = ta ? ta.value : state.markdown;
      try {
        await navigator.clipboard.writeText(text);
        const btn = host.querySelector<HTMLButtonElement>("#memoCopy")!;
        const orig = btn.textContent;
        btn.textContent = "Copied ✓";
        setTimeout(() => { btn.textContent = orig; }, 1400);
      } catch {
        prompt("Copy this memo:", text);
      }
    });

    host.querySelector<HTMLButtonElement>("#memoDownload")?.addEventListener("click", () => {
      const ta = host.querySelector<HTMLTextAreaElement>("#memoEditor");
      const text = ta ? ta.value : state.markdown;
      const blob = new Blob([text], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${state.active.kind}-${new Date().toISOString().slice(0, 10)}.md`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    });
  };

  // Bootstrap data + first render.
  Promise.all([loadGraph(), loadMovements(period)])
    .then(([graph, movements]) => {
      if (cancelled) return;
      state.graph = graph;
      state.ctxs = graph.topics.map((t) => composeTopic(t.id, graph)!).filter(Boolean);
      state.movements = movements;
      regenerate();
    })
    .catch((e) => {
      if (cancelled) return;
      host.innerHTML = `<div class="viz-empty">Memo data load failed: ${escapeHtml(String(e))}</div>`;
    });

  return () => {
    cancelled = true;
    host.innerHTML = "";
  };
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
